import { fetchUtils } from 'ra-core'
import simpleRestProvider from 'ra-data-simple-rest'
import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListParams,
  GetListResult,
  RaRecord,
  UpdateParams,
} from 'react-admin'
import { getAccessToken } from '../oidc'
import authToken from '../utils/authToken'
import { getEnvVariable } from '../utils/getEnvVariable'

// Dedupe concurrent silent renewals so parallel calls share one /token request.
let silentInFlight: Promise<string | undefined> | null = null

const ensureFreshToken = async (): Promise<string> => {
  if (!silentInFlight) {
    silentInFlight = getAccessToken().finally(() => {
      silentInFlight = null
    })
  }
  // Best-effort refresh; getAccessToken writes the fresh token to localStorage and is a no-op when OIDC isn't initialised.
  await silentInFlight
  const token = authToken.getToken()
  if (!token) {
    // Mimic original 412 reject shape — react-admin's checkError keys on `status` to decide logout.
    const err = new Error('No access token') as Error & { status: number }
    err.status = 412
    throw err
  }
  return token
}

export const httpClient = async (url: string, options: RequestInit = {}): Promise<{
  status: number;
  headers: Headers;
  body: string;
  json: unknown;
}> => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' })
    options.headers.set('accept-language', 'nl')
  }

  const token = await ensureFreshToken()
  const user = { authenticated: true, token }

  return fetchUtils.fetchJson(url, { ...options, user })
}

export const getUrl = (): string => getEnvVariable('REACT_APP_SERVER_URL', '')

// MCP gateway base URL used to build connect strings for AI clients. Mirrors the
// portal: prefer the REACT_APP_MCP_BASE_URL override (set to the cloudflared
// tunnel in local dev) so the URL is reachable from an external client, otherwise
// fall back to the gateway host (REACT_APP_SERVER_URI) or the current origin.
export const getMcpBaseUrl = (): string =>
  (getEnvVariable('REACT_APP_MCP_BASE_URL', '')
    || getEnvVariable('REACT_APP_SERVER_URI', window.location.origin)).replace(/\/$/, '')

export const getJson = async <T,>(url: string, options?: RequestInit): Promise<T> => {
  const response = await httpClient(url, options)
  return response.json as T
}

export const putJson = async <T,>(url: string, body: unknown): Promise<T> => {
  const response = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'accept-language': 'nl',
    }),
  })
  return response.json as T
}

export const postJson = async <T,>(url: string, body?: unknown): Promise<T> => {
  const response = await httpClient(url, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
    headers: new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'accept-language': 'nl',
    }),
  })
  return response.json as T
}

export const deleteJson = async (url: string): Promise<void> => {
  await httpClient(url, {
    method: 'DELETE',
    headers: new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'accept-language': 'nl',
    }),
  })
}

export const postFormData = async <T,>(url: string, formData: FormData): Promise<T> => {
  const token = authToken.getToken() || ''
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    headers: {
      'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
      'accept-language': 'nl',
    },
  })
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`)
  return response.json() as Promise<T>
}

export const uploadBrandingFile = async (
  settingsId: string,
  field: 'logo' | 'favicon',
  file: File,
): Promise<{ url: string }> => {
  const formData = new FormData()
  formData.append('file', file)
  const token = authToken.getToken() || ''
  const response = await fetch(
    `${getUrl()}/portal_settings/${settingsId}/upload_${field}`,
    {
      method: 'POST',
      headers: {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'accept-language': 'nl',
      },
      body: formData,
    },
  )
  if (!response.ok) {
    const error = new Error(`Upload failed: ${response.statusText}`) as Error & { status: number }
    error.status = response.status
    throw error
  }
  return response.json() as Promise<{ url: string }>
}

const dataProvider: DataProvider = simpleRestProvider(getUrl(), httpClient)

const createDocumentUpload = async (params: CreateParams) => {
  const formData = new FormData()
  const { files, expiration, ...fields } = params.data

  if (expiration && expiration !== 'never') {
    const days = parseInt(expiration as string, 10)
    const expiresAt = new Date(Date.now() + days * 86400000)
    formData.append('expires_at', expiresAt.toISOString())
  }

  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value))
    }
  })

  if (files) {
    const fileList = Array.isArray(files) ? files : [files]
    fileList.forEach((file: { rawFile?: File }) => {
      if (file.rawFile) {
        formData.append('files', file.rawFile)
      }
    })
  }

  const result = await postFormData<{ id: string }>(`${getUrl()}/document_upload`, formData)
  return { data: { ...result } }
}

const createApproveDenyHandler = (resource: string) => async (params: UpdateParams) => {
  const action = (params.meta as Record<string, string> | undefined)?.action
  if (action === 'approve') {
    const result = await postJson<{ id: string }>(`${getUrl()}/${resource}/${params.id}/approve`)
    return { data: { ...result } }
  }
  if (action === 'deny') {
    // eslint-disable-next-line camelcase
    const reviewNote = (params.data as Record<string, string>).review_note
    const result = await postJson<{ id: string }>(`${getUrl()}/${resource}/${params.id}/deny`, {
      // eslint-disable-next-line camelcase
      review_note: reviewNote,
    })
    return { data: { ...result } }
  }
  return dataProvider.update(resource, params)
}

const updatePlatformRequest = createApproveDenyHandler('platform_request')
const updateAccessRequest = createApproveDenyHandler('access_request')

const getApplicationHeaders = async (params: GetListParams): Promise<GetListResult<RaRecord>> => {
  // eslint-disable-next-line camelcase
  const { application_id: applicationId, ...restFilter } = (params.filter ?? {}) as Record<string, string>
  if (!applicationId) return { data: [], total: 0 }
  const page = params.pagination?.page ?? 1
  const perPage = params.pagination?.perPage ?? 100
  const field = params.sort?.field ?? 'header_name'
  const order = params.sort?.order ?? 'ASC'
  const start = (page - 1) * perPage
  const filterParam = Object.keys(restFilter).length > 0
    ? `&filter=${encodeURIComponent(JSON.stringify(restFilter))}`
    : ''
  const url = `${getUrl()}/application/${applicationId}/headers`
    + `?_sort=${field}&_order=${order}&_start=${start}&_end=${page * perPage}${filterParam}`
  const response = await httpClient(url)
  const total = parseInt(response.headers.get('content-range')?.split('/').pop() || '0', 10)
  return { data: response.json as RaRecord[], total }
}

const createApplicationHeader = async (params: CreateParams): Promise<CreateResult> => {
  const { application_id: applicationId, ...body } = params.data as Record<string, unknown>
  const url = `${getUrl()}/application/${applicationId as string}/headers`
  const result = await postJson<{ id: string }>(url, body)
  return { data: { ...result } }
}

export const xxllncDataProvider: DataProvider = {
  ...dataProvider,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getList: (resource: string, params: GetListParams): Promise<any> => {
    if (resource === 'application_header') return getApplicationHeaders(params)
    return dataProvider.getList(resource, params)
  },
  create: (resource: string, params: CreateParams): Promise<CreateResult> => {
    if (resource === 'document_upload') return createDocumentUpload(params) as Promise<CreateResult>
    if (resource === 'application_header') return createApplicationHeader(params)
    return dataProvider.create(resource, params)
  },
  update: (resource: string, params: UpdateParams) => {
    if (resource === 'access_request') return updateAccessRequest(params)
    if (resource === 'platform_request') return updatePlatformRequest(params)
    return dataProvider.update(resource, params)
  },
}
